import React, { useEffect, useState } from "react";
import { Card, Table, Button, Row, Col, Form, Image, Pagination } from '@themesberg/react-bootstrap';
import { Link, useHistory } from "react-router-dom";
import api from "../../utils/api";
import Swal from "sweetalert2";
import defaultIcon from "./../../assets/img/brand/logo2.png";

export default function NewsList() {
  const history = useHistory();
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/news`, { params: { page, perPage, search } });
      if (!data.error) {
        setItems(data.data);
        setTotal(data.total);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, [page, perPage]);

  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: 'Delete this news item?',
      text: 'This action cannot be undone.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, delete it',
      cancelButtonText: 'Cancel',
      heightAuto: false
    });
    if (!result.isConfirmed) return;
    try {
      await api.delete(`/news/${id}`);
      await Swal.fire({ title: 'Deleted!', text: 'News item was deleted.', icon: 'success', timer: 1500, showConfirmButton: false, heightAuto: false });
      fetchData();
    } catch (e) {
      await Swal.fire({ title: 'Error', text: e?.response?.data?.message || e.message || 'Failed to delete', icon: 'error', heightAuto: false });
    }
  };

  const totalPages = Math.ceil(total / perPage) || 1;

  const handleView = async (id) => {
    try {
      const { data } = await api.get(`/news/${id}`);
      const n = data.data || {};
      const statusLabel = n.status === '1' ? 'Active' : 'Inactive';
      const contentHtml = `
        <div style="text-align:left">
          ${
              n.uri
                ? `<img 
                    src="${n.uri.startsWith('http') ? n.uri : '/uploads/' + n.uri}" 
                    onerror="this.onerror=null; this.src='${defaultIcon}';"
                    style="width:130px;height:90px;object-fit:cover;border-radius:8px;margin-bottom:6px;" 
                  />`
                : `<img 
                    src="${defaultIcon}" 
                    style="width:130px;height:90px;object-fit:cover;border-radius:8px;margin-bottom:6px;" 
                  />`
            }
          <div style="margin-bottom:6px"><strong>Category:</strong> ${n.category || '-'}</div>
          <div style="margin-bottom:12px"><strong>Status:</strong> ${statusLabel}</div>
          <div style="white-space:pre-wrap;">${(n.content || '').toString()}</div>
        </div>
      `;
      await Swal.fire({
        title: n.title || 'News',
        html: contentHtml,
        // icon: 'info',
        width: 800,
        heightAuto: false
      });
    } catch (e) {
      await Swal.fire({ title: 'Error', text: e?.response?.data?.message || e.message || 'Failed to load news', icon: 'error', heightAuto: false });
    }
  };

  return (
    <Card className="shadow-sm">
      <Card.Header>
        <Row className="align-items-center">
          <Col><h5 className="mb-0">News</h5></Col>
          <Col className="text-end">
            <Button as={Link} to={`/news/new`} variant="primary">Create</Button>
          </Col>
        </Row>
        <Row className="mt-3">
          <Col md={6}>
            <Form.Control placeholder="Search..." value={search} onChange={(e) => setSearch(e.target.value)} />
          </Col>
          <Col md={2}>
            <Button variant="outline-secondary" onClick={() => { setPage(1); fetchData(); }}>Search</Button>
          </Col>
        </Row>
      </Card.Header>
      <Card.Body>
        <Table responsive hover className="align-items-center">
          <thead>
            <tr>
              <th>#</th>
              <th>Image</th>
              <th>Title</th>
              <th>Category</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.map((n, index) => (
              <tr key={n.id}>
                <td>{(page - 1) * perPage + index + 1}</td>
                <td><Image
                  src={n.uri || defaultIcon}
                  width={50}
                  height={'auto'}
                  rounded
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = defaultIcon;
                  }}
                />
                </td>
                <td>{n.title}</td>
                <td>{n.category || '-'}</td>
                <td>{n.status === '1' ? 'Active' : 'Inactive'}</td>
                <td>
                  <Button size="sm" variant="outline-info" className="me-2" onClick={() => handleView(n.id)}>View</Button>
                  <Button size="sm" variant="outline-secondary" as={Link} to={`/news/${n.id}/edit`} className="me-2">Edit</Button>
                  <Button size="sm" variant="outline-danger" onClick={() => handleDelete(n.id)}>Delete</Button>
                </td>
              </tr>
            ))}
            {items.length === 0 && (
              <tr><td colSpan={6} className="text-center">{loading ? 'Loading...' : 'No data'}</td></tr>
            )}
          </tbody>
        </Table>
        <div className="d-flex justify-content-between align-items-center">
          <Form.Select style={{ maxWidth: 120 }} value={perPage} onChange={(e) => { setPerPage(parseInt(e.target.value, 10)); setPage(1); }}>
            {[2, 10, 20, 50].map(n => <option key={n} value={n}>{n} / page</option>)}
          </Form.Select>
          <Pagination className="mb-0">

            {/* Prev */}
            <Pagination.Prev
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            />

            {/* Page numbers: previous */}
            {page > 1 && (
              <Pagination.Item onClick={() => setPage(page - 1)}>
                {page - 1}
              </Pagination.Item>
            )}

            {/* Active page */}
            <Pagination.Item active>{page}</Pagination.Item>

            {/* Page numbers: next */}
            {page < totalPages && (
              <Pagination.Item onClick={() => setPage(page + 1)}>
                {page + 1}
              </Pagination.Item>
            )}

            {/* Next */}
            <Pagination.Next
              disabled={page >= totalPages}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            />

          </Pagination>
        </div>
      </Card.Body>
    </Card>
  );
}


